import React from "react";
import { Box, Typography, IconButton, Chip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useCart } from "../../context/CartContext";
import formatCartItemDetails from "../../utils/formatCartItemDetails";
import CompactQuantitySelector from "../../components/cart/CompactQuantitySelector";
import QuantitySelector from "../../components/QuantitySelector";
import formatCategoryLabel from "../../utils/formatCategoryLabel";
import { useAuth } from "../../context/AuthContext";

const CartItemCard = ({
  cartItem,
  onDelete,
  variant = "preview",
  onQuantityChange,
}) => {
  const { discountedCartItems, updateCartItemQuantity, removeFromCart } =
    useCart();
  const { user, isAuthenticated } = useAuth();

  // Find the engine mirror by lineId
  const priced = discountedCartItems?.find(
    (it) => it.lineId === cartItem.lineId
  );

  // Overlay ONLY pricing/discount fields from the engine
  const item = {
    ...cartItem,
    ...(priced
      ? {
          unitPrice: priced.unitPrice,
          totalPrice: priced.totalPrice,
          discountAmount: priced.discountAmount,
          discountType: priced.discountType,
          discountName: priced.discountName,
          discountsApplied: priced.discountsApplied,
        }
      : {}),
  };

  const {
    lineId,
    displayName,
    image,
    quantity = 1,
    itemsPerSet,
    discountAmount = 0,
    discountType = null,
  } = item;

  // Price math (with safe fallbacks)
  const discountedUnit =
    typeof item.unitPrice === "number" ? item.unitPrice : 0;

  const discountedTotal =
    typeof item.totalPrice === "number"
      ? item.totalPrice
      : discountedUnit * quantity;

  const hasDiscount = Number(discountAmount) > 0;
  const originalTotal = hasDiscount
    ? +(discountedTotal + Number(discountAmount)).toFixed(2)
    : discountedTotal;

  const originalUnit = quantity ? +(originalTotal / quantity).toFixed(2) : 0;
  const effectiveUnitPrice = quantity
    ? +(discountedTotal / quantity).toFixed(2)
    : 0;

  const details = formatCartItemDetails(item);

  const isCartPage = variant === "cartPage";
  const isCheckout = variant === "checkout";
  const minQty = itemsPerSet || 1;
  const showPerUnitPrice = quantity > 1;

  const handleQuantityChange = (newQty) => {
    if (onQuantityChange) onQuantityChange(newQty);
    else updateCartItemQuantity(lineId, newQty);
  };

  const handleDelete = onDelete || (() => removeFromCart(lineId));

  // Discount chip label
  const discountChipLabel =
    hasDiscount &&
    (discountType === "percentage"
      ? `${Math.round(
          (Number(discountAmount) / (originalTotal || 1)) * 100
        )}% OFF`
      : `$${Number(discountAmount).toFixed(2)} OFF`);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 3,
        mb: 3,
        pb: 3,
        borderBottom: "1px solid #eee",
        alignItems: "flex-start",
      }}>
      {/* Image */}
      <Box
        component="img"
        src={image || "/images/placeholder.png"}
        alt={displayName || ""}
        sx={{
          width: isCartPage ? 120 : 80,
          height: isCartPage ? 120 : 80,
          objectFit: "cover",
          borderRadius: 2,
          backgroundColor: "#f6f6f6",
        }}
      />

      <Box sx={{ flexGrow: 1 }}>
        {/* Header: category label + discount chip + delete */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="primary"
              sx={{ fontSize: 15 }}>
              {isCheckout
                ? `${formatCategoryLabel(item)} × ${quantity}`
                : formatCategoryLabel(item)}
            </Typography>
            {hasDiscount && discountChipLabel && (
              <Chip label={discountChipLabel} color="success" size="small" />
            )}
          </Box>

          {!isCheckout && (
            <IconButton size="small" onClick={handleDelete} aria-label="Remove">
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Details (size, color, flavor, etc.) */}
        {details.map(({ label, value, isColor }, idx) => (
          <Box
            key={idx}
            sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{label}:</strong>
            </Typography>
            {isColor ? (
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  backgroundColor: value,
                  border: "1px solid #ccc",
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {value}
              </Typography>
            )}
          </Box>
        ))}

        {/* Quantity Selector */}
        {!isCheckout && (
          <Box sx={{ mt: 1 }}>
            {isCartPage ? (
              <QuantitySelector
                value={quantity}
                setValue={handleQuantityChange}
                min={minQty}
                item={item}
                sync={!!(user && isAuthenticated)}
              />
            ) : (
              <CompactQuantitySelector
                quantity={quantity}
                onChange={handleQuantityChange}
                itemsPerSet={minQty}
                item={item}
                sync={!!(user && isAuthenticated)}
              />
            )}
          </Box>
        )}

        {/* Price */}
        <Box sx={{ mt: 0.5 }}>
          <Typography fontSize={13}>
            <strong>Total: ${discountedTotal.toFixed(2)} CAD</strong>
            {hasDiscount && (
              <Typography
                component="span"
                fontSize={13}
                color="text.secondary"
                sx={{ textDecoration: "line-through", ml: 1 }}>
                ${originalTotal.toFixed(2)}
              </Typography>
            )}
          </Typography>

          {showPerUnitPrice && (
            <Typography fontSize={12} color="text.secondary" sx={{ mt: 0.25 }}>
              {hasDiscount ? (
                <>
                  ${effectiveUnitPrice.toFixed(2)} each
                  <Typography
                    component="span"
                    fontSize={12}
                    color="text.secondary"
                    sx={{ textDecoration: "line-through", ml: 1 }}>
                    ${originalUnit.toFixed(2)}
                  </Typography>
                </>
              ) : (
                <>${effectiveUnitPrice.toFixed(2)} each</>
              )}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CartItemCard;
